/**
 * PvP Roulette — централизованные константы.
 * Единственный источник истины для всех магических чисел в домене рулетки.
 */

// ─── Timing ────────────────────────────────────────────────────────────────

/**
 * Время жизни «оптимистичной» ставки на клиенте (мс).
 * Если за 15 секунд бэкенд не подтвердил ставку через WS — она удаляется локально.
 */
export const PENDING_TTL_MS = 15_000

/** TTL для «висящего» оптимистичного трика, пока WS не прислал его в `round.tricks` (мс). */
export const OPTIMISTIC_TRICK_TTL_MS = 12_000

/**
 * Длительность tween-анимации при изменении веса сектора (мс).
 * Когда приходит новая ставка, сектор плавно расширяется за это время.
 */
export const SECTOR_TWEEN_DURATION_MS = 200

/** Плавный переход цветов чужих секторов при входе в TRICKS (мс). */
export const TRICKS_SECTOR_COLOR_TWEEN_MS = 450

/**
 * Константы профиля скорости колеса. Единственный источник истины.
 *
 * Фаза SPINNING:
 *   0 → SPINNING_PEAK_REV_PER_SEC  за SPINNING_RAMP_UP_MS  (быстрый разгон)
 *   SPINNING_PEAK_REV_PER_SEC → TRICKS_START_REV_PER_SEC  линейно за оставшееся время SPINNING
 *
 * Фаза TRICKS:
 *   TRICKS_START_REV_PER_SEC → TRICKS_END_REV_PER_SEC  линейно за всё время TRICKS
 *   (задай одинаковые значения — скорость будет постоянной)
 *
 * Фаза SLOWING:
 *   ease-out от текущей скорости до 0; длительность дуги задаётся только сервером
 *   (`FinalizeToTarget.payload.durationMs` = остаток до визуальной остановки, см. reduceRoomState).
 */

/** Время разгона в начале фазы SPINNING до пиковой скорости (мс). */
export const SPINNING_RAMP_UP_MS = 50

/** Пиковая угловая скорость в фазе SPINNING (оборотов/сек). */
export const SPINNING_PEAK_REV_PER_SEC = 5.0

/** Скорость в начале фазы TRICKS — до неё линейно замедляемся из SPINNING (оборотов/сек). */
export const TRICKS_START_REV_PER_SEC = 1.17

/** Скорость в конце фазы TRICKS (оборотов/сек). Целевая скорость входа в SLOWING. */
export const TRICKS_END_REV_PER_SEC = 0.36

/**
 * Safety-buffer для UI в фазе TRICKS (мс): визуально закрываем окно триков раньше server deadline.
 * Применяется только к countdown/блокировке кнопок, серверный дедлайн не меняется.
 */
export const TRICKS_VISUAL_END_LEAD_MS = 400

/**
 * Показатель степени кривой затухания скорости в фазе TRICKS.
 * speed(t) = lerp(TRICKS_START, TRICKS_END, t^TRICKS_DECAY_EXPONENT)
 * Значение < 1 делает спад быстрее в начале фазы (более "крутая" кривая).
 */
export const TRICKS_DECAY_EXPONENT = 0.75

/**
 * Минимальное изменение длины стенового окна TRICKS (мс: от `tricksPhaseStartTimeWallMs` до
 * `tricks_ends_at`), при котором `WheelEngine.updateTricksDurationKeepingVelocity` заново
 * подгоняет `tricksEntryVelDegPerMs`. Повторный вызов с тем же окном (другой трик, TrickUpdated,
 * echo после оптимистичного push) иначе пере-солвит кривую по `computeSpinningVelocity` без
 * push-overlay — визуальный провал скорости.
 */
export const TRICKS_WALL_DURATION_RETUNE_MIN_DELTA_MS = 4

/**
 * Целевой показатель степени кривой замедления в фазе SLOWING.
 * Используется при вычислении delta (числа градусов) так, чтобы exp ≈ этому значению.
 * exp=2 соответствует линейному торможению (visual decel = constant).
 */
export const SLOWING_TARGET_EXP = 4

/**
 * Пиковая угловая скорость буста от трика-толчка (градусов/сек).
 * После трика скорость мгновенно прыгает до этого значения и линейно гасится за TRICK_BOOST_DURATION_MS.
 */
export const TRICK_BOOST_PEAK_DEG_PER_SEC = 950

/** Длительность спада скоростного буста от трика (мс). */
export const TRICK_BOOST_DURATION_MS = 1_400

/**
 * FREEZE_WHEEL — множитель мгновенной угловой скорости на время эффекта (доля от «текущей»).
 * CONFIG: `freeze_factor: 0.45` → 45% скорости во время freeze.
 * `freeze_allowed_only_in_push_phase` — на клиенте трик приходит только в фазе `TRICKS`.
 */
export const TRICK_FREEZE_FACTOR = 0.45

/** Длительность freeze (мс). CONFIG: `freeze_duration: 0.5` с. */
export const TRICK_FREEZE_DURATION_MS = 500

/** CONFIG: `max_freezes_per_round` — лимит на бэкенде; референс для контракта/доков. */
export const TRICK_FREEZE_MAX_PER_ROUND = 5

/**
 * Если с сервера не пришёл tricks_ends_at, движок всё равно запланирует торможение
 * через столько мс после входа в TRICKS (избегает вечного SPINNING/TRICKS без SLOWING).
 */
export const TRICKS_CLIENT_SCHEDULE_FALLBACK_MS = 12_000

/**
 * Таймаут ожидания первого WS-события со снапшотом (мс).
 * Если за это время Centrifugo не прислал ничего — делаем GET /current-round напрямую.
 */
export const INITIAL_SNAPSHOT_TIMEOUT_MS = 500

// ─── Wheel geometry ────────────────────────────────────────────────────────

/** Размер canvas колеса по умолчанию (CSS-пиксели). */
export const WHEEL_DEFAULT_SIZE = 320

/** Максимальный размер canvas колеса (CSS-пиксели). Ограничение для больших экранов. */
export const WHEEL_MAX_SIZE = 420

/** Внешний радиус колеса как доля от размера canvas (0–1). */
export const WHEEL_OUTER_RADIUS_RATIO = 0.46

/** Внутренний радиус ступицы как доля от размера canvas (0–1). */
export const WHEEL_INNER_RADIUS_RATIO = 0.19

/**
 * Отступ внешнего края **цветных секторов** от геометрического `outerRadius` (px).
 * Внешний радиус сектора = `outerRadius - WHEEL_SECTOR_OUTER_INSET_PX`.
 * Меньше значение → секторы чуть шире; PNG-обод (`roulette-background.png`) не масштабируется.
 * Не путать с `WHEEL_OUTER_RADIUS_RATIO` (меняет и фон, и секторы).
 */
export const WHEEL_SECTOR_OUTER_INSET_PX = 4

/**
 * Знак приращения «логического» угла колеса в `WheelEngine` (градусы за кадр).
 * При `rotation = -visualAngle` на контейнере: `+1` — визуально против часовой при росте угла; `-1` — по часовой.
 * Одна константа для SPINNING/TRICKS, триков и ветки `FinalizeToTarget` (дельта до target того же знака).
 */
export const WHEEL_VISUAL_SPIN_SIGN = -1 as const

/** Знак вращения, под который сверстаны Spine-анимации PUSH (`fire_*`) на front-слое. */
export const PUSH_TRICK_VFX_AUTHORED_SPIN_SIGN = 1 as const

// ─── Easing keys ───────────────────────────────────────────────────────────

/**
 * Реестр ключей easing-функций для анимаций колеса.
 * Используется в PixiCommands (поле `easingKey`) и в `EASING_PRESETS`.
 * Вынесен сюда, чтобы entities (reducer) и features (engine) использовали
 * одни и те же строки без перекрёстных импортов между слоями.
 */
export const EASING_KEY = {
  /** Равномерное движение без ускорения/замедления. */
  LINEAR: "linear",
  /** Плавное замедление в конце (кубическая кривая). */
  EASE_OUT_CUBIC: "easeOutCubic",
  /** Плавный старт и финиш (кубическая кривая). */
  EASE_IN_OUT_CUBIC: "easeInOutCubic",
  /** Сильное плавное замедление в конце (квинтическая кривая). */
  EASE_OUT_QUINT: "easeOutQuint",
  /** Фаза разгона — используется в StartSpin. */
  SPIN_IN: "spin_in",
  /** Фаза торможения — FinalizeToTarget (WheelEngine: 1−(1−t)^p, p зависит от текущей скорости). */
  SPIN_OUT: "spin_out",
  /** Резкий переход — для tween-анимаций веса секторов. */
  UI_FAST: "ui_fast",
} as const

export type EasingKey = (typeof EASING_KEY)[keyof typeof EASING_KEY]

// ─── Defaults ──────────────────────────────────────────────────────────────

/**
 * Источник банка по умолчанию для расчёта призового фонда.
 * `'parties'` — банк считается из суммарных ставок участников (parties).
 */
export const DEFAULT_BANK_SOURCE = "parties" as const

/** ID группы по умолчанию для командных ставок. */
export const DEFAULT_GROUP_ID = "Team 1" as const

/** Цвет команды 1 по умолчанию (синий, 0x3b82f6) — применяется для секторов колеса и UI. */
export const TEAM_1_COLOR = 0x3b82f6

/** Current user's SOLO sector color and side glow. */
export const SOLO_CURRENT_USER_COLOR = 0x00fe74

/**
 * Поворот паттерна против часовой стрелки (градусы).
 */
export const TEAM_PATTERN_ROTATION_DEG = 9.76

/**
 * Дополнительный поворот UV для командного оверлея с `textureSpace: 'global'` (цепочка Pixi
 * инвертирует style.matrix до scale(1/sourceSize)). Подстройте, если сетка «уехала» по углу.
 */
export const TEAM_PATTERN_GLOBAL_UV_ROTATION_OFFSET_DEG = 0

export const TEAM_PATTERN_BACKGROUND_COLOR = 0xffcc33

export const TEAM_PATTERN_ICON_WIDTH = 78
export const TEAM_PATTERN_ICON_HEIGHT = 18
export const TEAM_PATTERN_GAP_X = 8
export const TEAM_PATTERN_GAP_Y = 8

export const TEAM_PATTERN_TILE_WIDTH =
  TEAM_PATTERN_ICON_WIDTH + TEAM_PATTERN_GAP_X

export const TEAM_PATTERN_TILE_HEIGHT =
  TEAM_PATTERN_ICON_HEIGHT + TEAM_PATTERN_GAP_Y
